import { useCallback, useEffect, useRef, useState } from "react";
import { getCorpusFeedStatistics } from "../../../../lib/corpusApi";
import { useForegroundInterval } from "../../../../hooks/useForegroundInterval";
import type { FeedStatisticsLookup } from "../../../../components/domain/data-display/FeedGrid";
import type { CorpusFeed, CorpusFeedStatisticsByFeed } from "@shared/types/corpus/api.ts";

// How often the feeds with a run in progress are asked about again.
const OPEN_RUN_REFRESH_MS = 10_000;

export interface UseFeedStatisticsResult extends FeedStatisticsLookup {
  /** Asks again for the statistics of these feeds, for after something changed them. */
  reload: (feedIds: string[]) => Promise<void>;
}

/**
 * Loads the statistics of the feeds listed, one request for each new batch of them, and keeps those
 * of a feed with a run in progress fresh while the tab is open. The feeds themselves never wait for
 * this: a failure only marks their statistics unavailable.
 */
export function useFeedStatistics(feeds: CorpusFeed[]): UseFeedStatisticsResult {
  const [byFeed, setByFeed] = useState<CorpusFeedStatisticsByFeed>({});
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(new Set());
  const requested = useRef(new Set<string>());

  const load = useCallback(async (ids: string[]) => {
    try {
      const fresh = await getCorpusFeedStatistics(ids);
      setByFeed((current) => ({ ...current, ...fresh }));
      setUnavailable((current) => new Set([...current].filter((id) => !ids.includes(id))));
    } catch {
      setUnavailable((current) => new Set([...current, ...ids]));
    }
  }, []);

  useEffect(() => {
    const fresh = feeds.map((feed) => feed.id).filter((id) => !requested.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => requested.current.add(id));
    void load(fresh);
  }, [feeds, load]);

  const openRunIds = Object.entries(byFeed)
    .filter(([, statistics]) => statistics.openRun !== null)
    .map(([id]) => id);
  useForegroundInterval(() => void load(openRunIds), OPEN_RUN_REFRESH_MS, {
    enabled: openRunIds.length > 0,
  });

  return { byFeed, unavailable, reload: load };
}
